import { For, Show } from "solid-js";
import { metrics } from "../../stores/metrics.ts";
import { connected } from "../../stores/connection.ts";
import { alerts } from "../../stores/alerts.ts";
import { GlowIndicator } from "../theme/GlowIndicator.tsx";
import type { MetricSource } from "@houston/shared";

const SOURCES: { id: MetricSource; label: string }[] = [
  { id: "graphql-gateway", label: "GQL" },
  { id: "prismic", label: "PRS" },
  { id: "elasticsearch", label: "ES" },
  { id: "fastly", label: "CDN" },
  { id: "newrelic-apm", label: "APM" },
  { id: "newrelic-browser", label: "RUM" },
  { id: "mux", label: "MUX" },
  { id: "sailthru", label: "STU" },
  { id: "newrelic-ads", label: "ADS" },
  { id: "tedwise", label: "AI" },
];

function getOverallStatus() {
  if (alerts.active.some((a) => a.severity === "critical")) return "critical";
  if (alerts.active.some((a) => a.severity === "warning")) return "degraded";
  return "nominal";
}

export function StatusStrip() {
  const status = () => getOverallStatus();

  const statusColor = () => {
    switch (status()) {
      case "critical": return "text-[var(--color-critical)] text-glow-critical";
      case "degraded": return "text-[var(--color-warning)] text-glow-warning";
      default: return "text-[var(--color-healthy)] text-glow-healthy";
    }
  };

  const statusLabel = () => {
    switch (status()) {
      case "critical": return "CONDITION RED";
      case "degraded": return "DEGRADED";
      default: return "ALL SYSTEMS NOMINAL";
    }
  };

  return (
    <div class="flex items-center gap-0 rounded-md overflow-hidden border border-[var(--color-border-panel)] bg-[var(--color-bg-panel)]">
      {/* Logo block */}
      <div class="flex items-center gap-3 border-r border-[rgba(255,255,255,0.04)] px-5 py-2.5">
        <div class="flex flex-col">
          <span class="font-display text-[11px] font-black uppercase tracking-[0.3em] text-[var(--color-info)] text-glow-info">
            Houston
          </span>
          <span class="font-mono text-[8px] uppercase tracking-[0.2em] text-[var(--color-text-dim)]">
            Mission Control
          </span>
        </div>
      </div>

      {/* System status */}
      <div class="flex items-center gap-2.5 border-r border-[rgba(255,255,255,0.04)] px-4 py-2.5">
        <div class={`h-2 w-2 rounded-full animate-beacon ${status() === "nominal" ? "bg-[var(--color-healthy)]" : status() === "degraded" ? "bg-[var(--color-warning)]" : "bg-[var(--color-critical)]"}`} />
        <span class={`font-display text-[10px] font-bold uppercase tracking-[0.15em] ${statusColor()}`}>
          {statusLabel()}
        </span>
      </div>

      {/* Connection status */}
      <div class="flex items-center gap-2 border-r border-[rgba(255,255,255,0.04)] px-4 py-2.5">
        <div class={`h-1.5 w-1.5 rounded-full ${connected() ? "bg-[var(--color-healthy)] animate-beacon" : "bg-[var(--color-critical)] animate-beacon"}`} />
        <span class={`font-mono text-[9px] uppercase tracking-wider ${connected() ? "text-[var(--color-healthy)]" : "text-[var(--color-critical)]"}`}>
          {connected() ? "LIVE" : "RECONNECT"}
        </span>
      </div>

      {/* Alert count */}
      <Show when={alerts.active.length > 0}>
        <div class="flex items-center gap-2 border-r border-[rgba(255,255,255,0.04)] px-4 py-2.5">
          <span class="font-display text-[10px] font-bold text-[var(--color-critical)] text-glow-critical">
            {alerts.active.length}
          </span>
          <span class="font-mono text-[9px] uppercase tracking-wider text-[var(--color-text-dim)]">
            ALERTS
          </span>
        </div>
      </Show>

      {/* Subsystem indicators */}
      <div class="ml-auto flex items-center gap-2.5 px-4 py-2.5">
        <For each={SOURCES}>
          {(s) => (
            <GlowIndicator
              label={s.label}
              status={metrics.health[s.id]?.status ?? "unknown"}
            />
          )}
        </For>
      </div>
    </div>
  );
}
